import { useEffect, useState } from "react";
import { forgetSessionUser, readSessionUser, StoredUser, userStateListener } from "./firebase";

export type Session =
  | { status: "loading" }
  | { status: "signedOut" }
  | { status: "signedIn"; user: StoredUser | null };

const storedCopyFor = (uid: string): StoredUser | null => {
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "null");
    return stored?.uid === uid ? stored : null;
  } catch {
    return null;
  }
};

// Follows Firebase's own sign-in, so the dashboard never outlives it, and re-reads the
// person's account once per page load so a changed role takes effect.
export function useSession(): Session {
  const [session, setSession] = useState<Session>({ status: "loading" });

  useEffect(() => {
    let latest = 0;
    const unsubscribe = userStateListener(async (user) => {
      const call = ++latest;
      if (!user) {
        forgetSessionUser();
        setSession({ status: "signedOut" });
        return;
      }
      // If the account can't be read (offline, say), carry on with what was kept for this person.
      const stored = await readSessionUser(user).catch(() => storedCopyFor(user.uid));
      if (call === latest) setSession({ status: "signedIn", user: stored });
    });
    return () => {
      latest = -1;
      unsubscribe();
    };
  }, []);

  return session;
}
